import isEmpty from 'lodash/isEmpty';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MenuItemIndividualItem } from '../../../../atomic/molecules/Dropdown_V2/interfaces';
import { useGetMembersFromCriteria } from '../../../../hooks/useMembersSearch';
import { useFetchFlowDetailsQuery } from '../../../../queries/Flows/Feed';
import { useProfileInfoFetchQuery } from '../../../../queries/Profile';
import useFlowBuilderStore from '../../../../stores/flowBuilderStore';
import { blockDataSelector } from '../../../../stores/flowBuilderStore/selectors';
import { mapParticipationAndVisibilityDataToPreviewRules } from '../../../../Utils/flows';
import { mapRulesFromCriteriaGroups } from '../../../../Utils/flows/criteria';
import { allPermissionAccessPermission, ShareSheetPermissions } from './data';

export const useShareSheetAccessPreviewController = () => {
  const blockData = useFlowBuilderStore(blockDataSelector);

  const { flowId } = useParams<{ flowId: string }>();

  const [dropdownValue, setDropdownValue] = useState<MenuItemIndividualItem>(
    allPermissionAccessPermission,
  );

  const visibilityBlockData = blockData.VISIBILITY;
  const participantsBlockData = blockData.PARTICIPANTS;

  const { participantsCriteria } = participantsBlockData;
  // @ts-ignore
  const { criteriaGroups: visibilityCriteria } = visibilityBlockData;

  const { data: profileData } = useProfileInfoFetchQuery();

  const { data: flowDetails } = useFetchFlowDetailsQuery(
    flowId || '',
    'builder',
  );

  const participantRulesFromCriteria = useMemo(
    () =>
      !isEmpty(participantsCriteria)
        ? mapRulesFromCriteriaGroups(participantsCriteria, undefined, true)
        : undefined,
    [participantsCriteria],
  );

  const visibilityRulesFromCriteria = useMemo(
    () => mapRulesFromCriteriaGroups(visibilityCriteria, undefined, true),
    [visibilityCriteria],
  );

  const {
    models: {
      isLoading: isParticipantsSelectedLoading,
      searchedMembers: participantsSelected,
      hasMoreMembers: hasMoreParticipantsSelected,
    },
    operations: { fetchMoreMembers: fetchMoreParticipantsSelected },
  } = useGetMembersFromCriteria(participantRulesFromCriteria, undefined, {
    isNewMembersAdded: true,
  });

  const {
    models: {
      isLoading: isMembersWithVisibilityLoading,
      searchedMembers: membersWithVisibility,
      hasMoreMembers: hasMoreMembersWithVisibility,
    },
    operations: { fetchMoreMembers: fetchMoreMembersWithVisibility },
  } = useGetMembersFromCriteria(visibilityRulesFromCriteria, undefined, {
    isNewMembersAdded: true,
  });

  const fetchMoreMembers = () => {
    if (
      dropdownValue.id === ShareSheetPermissions.allPermissions ||
      dropdownValue.id === ShareSheetPermissions.canPost
    ) {
      fetchMoreParticipantsSelected();
    }
    if (
      dropdownValue.id === ShareSheetPermissions.allPermissions ||
      dropdownValue.id === ShareSheetPermissions.canView
    ) {
      fetchMoreMembersWithVisibility();
    }
  };

  const previewMembers = useMemo(() => {
    if (isParticipantsSelectedLoading || isMembersWithVisibilityLoading) {
      return [];
    }
    return mapParticipationAndVisibilityDataToPreviewRules({
      participants: participantsSelected || [],
      membersWithVisibility: membersWithVisibility || [],
      flowOwner: flowDetails?.data?.owner,
      displayType: `${dropdownValue.id}`,
    });
  }, [
    dropdownValue.id,
    flowDetails?.data?.owner,
    isMembersWithVisibilityLoading,
    isParticipantsSelectedLoading,
    membersWithVisibility,
    participantsSelected,
  ]);

  const currentUserId = useMemo(
    () => profileData?.member?.memberId,
    [profileData],
  );

  return {
    previewMembers,
    dropdownValue,
    setDropdownValue,
    fetchMoreMembers,
    hasMoreUsersToFetch:
      hasMoreMembersWithVisibility || hasMoreParticipantsSelected,
    fetchMoreParticipantsSelected,
    currentUserId,
  };
};
